import { isDevelopment } from "appConfiguration";
import type { BRCYCodes, BrandTwoLetterCode } from "brandRegionConfig";
import { useAppConfiguration } from "core/context/AppConfigurationContext";
import type { CentrixSchemas } from "core/entities/CentrixTypes";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import type { StaticImageData } from "next/image";
import NextImage from "next/image";
import CRDesktop from "public/logos/default-brand-logos/CR/desktop.png";
import CREmailSignature from "public/logos/default-brand-logos/CR/email-signature.png";
import CRMobile from "public/logos/default-brand-logos/CR/mobile.png";
import FADesktop from "public/logos/default-brand-logos/FA/desktop.png";
import FAEmailSignature from "public/logos/default-brand-logos/FA/email-signature.png";
import FAEmailSignatureEv from "public/logos/default-brand-logos/FA/email-signature-ev.png";
import FAMobile from "public/logos/default-brand-logos/FA/mobile.png";
import NCDesktop from "public/logos/default-brand-logos/NC/desktop.png";
import NCEmailSignature from "public/logos/default-brand-logos/NC/email-signature.png";
import NCMobile from "public/logos/default-brand-logos/NC/mobile.png";
import NGDesktop from "public/logos/default-brand-logos/NG/desktop.png";
import NGEmailSignature from "public/logos/default-brand-logos/NG/email-signature.png";
import NGMobile from "public/logos/default-brand-logos/NG/mobile.png";
import NRDesktop from "public/logos/default-brand-logos/NR/desktop.png";
import NREmailSignature from "public/logos/default-brand-logos/NR/email-signature.png";
import NRMobile from "public/logos/default-brand-logos/NR/mobile.png";
import PCDesktop from "public/logos/default-brand-logos/PC/desktop.png";
import PCEmailSignature from "public/logos/default-brand-logos/PC/email-signature.png";
import PCMobile from "public/logos/default-brand-logos/PC/mobile.png";
import SADesktop from "public/logos/default-brand-logos/SA/desktop.png";
import SAEmailSignature from "public/logos/default-brand-logos/SA/email-signature.png";
import SAMobile from "public/logos/default-brand-logos/SA/mobile.png";
import SGDesktop from "public/logos/default-brand-logos/SG/desktop.png";
import SGEmailSignature from "public/logos/default-brand-logos/SG/email-signature.png";
import SGMobile from "public/logos/default-brand-logos/SG/mobile.png";
import {
  type DetailedHTMLProps,
  type ImgHTMLAttributes,
  type Ref,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type { AvailableLogoPath } from "resources/availableLogoPath";

export type LogoType = Exclude<
  CentrixSchemas["Fixhub.PitstopLogos.PitstopLogoType"],
  "Invalid"
>;
export type PitstopLogoUrlsDto =
  CentrixSchemas["Fixhub.PitstopLogos.PitstopLogoUrlsDto"];

export type PitstopLogoUrlPropertyKeys = Exclude<
  keyof PitstopLogoUrlsDto,
  "brcyCode" | "locale"
>;

type FallbackLogoConfigEntry = Record<
  LogoType,
  { staticImage: StaticImageData; path: AvailableLogoPath }
>;

const fallbackConfigMap: Record<
  BrandTwoLetterCode,
  Omit<FallbackLogoConfigEntry, "Invalid">
> = {
  FA: {
    Desktop: {
      staticImage: FADesktop,
      path: "/logos/default-brand-logos/FA/desktop.png",
    },
    Mobile: {
      staticImage: FAMobile,
      path: "/logos/default-brand-logos/FA/mobile.png",
    },
    EmailSignature: {
      staticImage: FAEmailSignature,
      path: "/logos/default-brand-logos/FA/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: FAEmailSignatureEv,
      path: "/logos/default-brand-logos/FA/email-signature-ev.png",
    },
  },
  SG: {
    Desktop: {
      staticImage: SGDesktop,
      path: "/logos/default-brand-logos/SG/desktop.png",
    },
    Mobile: {
      staticImage: SGMobile,
      path: "/logos/default-brand-logos/SG/mobile.png",
    },
    EmailSignature: {
      staticImage: SGEmailSignature,
      path: "/logos/default-brand-logos/SG/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: SGEmailSignature,
      path: "/logos/default-brand-logos/SG/email-signature.png",
    },
  },
  SA: {
    Desktop: {
      staticImage: SADesktop,
      path: "/logos/default-brand-logos/SA/desktop.png",
    },
    Mobile: {
      staticImage: SAMobile,
      path: "/logos/default-brand-logos/SA/mobile.png",
    },
    EmailSignature: {
      staticImage: SAEmailSignature,
      path: "/logos/default-brand-logos/SA/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: SAEmailSignature,
      path: "/logos/default-brand-logos/SA/email-signature.png",
    },
  },
  NG: {
    Desktop: {
      staticImage: NGDesktop,
      path: "/logos/default-brand-logos/NG/desktop.png",
    },
    Mobile: {
      staticImage: NGMobile,
      path: "/logos/default-brand-logos/NG/mobile.png",
    },
    EmailSignature: {
      staticImage: NGEmailSignature,
      path: "/logos/default-brand-logos/NG/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: NGEmailSignature,
      path: "/logos/default-brand-logos/NG/email-signature.png",
    },
  },
  NC: {
    Desktop: {
      staticImage: NCDesktop,
      path: "/logos/default-brand-logos/NC/desktop.png",
    },
    Mobile: {
      staticImage: NCMobile,
      path: "/logos/default-brand-logos/NC/mobile.png",
    },
    EmailSignature: {
      staticImage: NCEmailSignature,
      path: "/logos/default-brand-logos/NC/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: NCEmailSignature,
      path: "/logos/default-brand-logos/NC/email-signature.png",
    },
  },
  PC: {
    Desktop: {
      staticImage: PCDesktop,
      path: "/logos/default-brand-logos/PC/desktop.png",
    },
    Mobile: {
      staticImage: PCMobile,
      path: "/logos/default-brand-logos/PC/mobile.png",
    },
    EmailSignature: {
      staticImage: PCEmailSignature,
      path: "/logos/default-brand-logos/PC/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: PCEmailSignature,
      path: "/logos/default-brand-logos/PC/email-signature.png",
    },
  },
  CR: {
    Desktop: {
      staticImage: CRDesktop,
      path: "/logos/default-brand-logos/CR/desktop.png",
    },
    Mobile: {
      staticImage: CRMobile,
      path: "/logos/default-brand-logos/CR/mobile.png",
    },
    EmailSignature: {
      staticImage: CREmailSignature,
      path: "/logos/default-brand-logos/CR/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: CREmailSignature,
      path: "/logos/default-brand-logos/CR/email-signature.png",
    },
  },
  NR: {
    Desktop: {
      staticImage: NRDesktop,
      path: "/logos/default-brand-logos/NR/desktop.png",
    },
    Mobile: {
      staticImage: NRMobile,
      path: "/logos/default-brand-logos/NR/mobile.png",
    },
    EmailSignature: {
      staticImage: NREmailSignature,
      path: "/logos/default-brand-logos/NR/email-signature.png",
    },
    EmailSignatureEv: {
      staticImage: NREmailSignature,
      path: "/logos/default-brand-logos/NR/email-signature.png",
    },
  },
};

const TYPE_FILENAME_MAP: Record<LogoType, string> = {
  Desktop: "desktop.png",
  Mobile: "mobile.png",
  EmailSignature: "email-signature.png",
  EmailSignatureEv: "email-signature-ev.png",
};

export function getExpectedLogoUrl(
  brcyCode: string,
  locale: string,
  logoType: LogoType,
  logoBaseUrl: string
) {
  const segments = [
    "logos",
    brcyCode.toLowerCase(),
    locale.toLowerCase(),
    TYPE_FILENAME_MAP[logoType],
  ];
  if (isDevelopment) {
    // Azurite expects the $web segment, whereas AzureBlobStorage does not. This is a workaround to make it work in both environments.
    segments.unshift("$web");
  }
  try {
    const url = new URL(segments.join("/"), logoBaseUrl).toString();
    return url;
  } catch {
    return "";
  }
}

const TYPE_PROP_MAP: Record<LogoType, PitstopLogoUrlPropertyKeys> = {
  Desktop: "desktopUrl",
  Mobile: "mobileUrl",
  EmailSignature: "emailSignatureUrl",
  EmailSignatureEv: "emailSignatureEvUrl",
};

export function getLogoUrlPropFromType(logoType: LogoType) {
  return TYPE_PROP_MAP[logoType];
}

export function useLogos(brcyCode: BRCYCodes, locale: string) {
  const config = useAppConfiguration();
  const logoBaseUrl =
    "NEXT_PUBLIC_LOGO_BASE_URL" in config
      ? config.NEXT_PUBLIC_LOGO_BASE_URL
      : undefined;
  const brandTwoLetterCode = brcyCode.slice(0, 2) as BrandTwoLetterCode;
  const brandFallback = fallbackConfigMap[brandTwoLetterCode];
  const getLogoUrl = useCallback(
    (logoType: LogoType) =>
      getExpectedLogoUrl(brcyCode, locale, logoType, logoBaseUrl || ""),
    [brcyCode, locale, logoBaseUrl]
  );
  const apiData = useCentrixApi({
    path: "/api/app/pitstop-logos/logo-urls",
    parameters: { query: { brcyCode: brcyCode.toUpperCase(), locale } },
  });
  return {
    apiData,
    fallback: brandFallback,
    getExpectedLogoUrl: getLogoUrl,
  };
}

export function useLogoImageInfo(
  brcyCode: BRCYCodes,
  locale: string,
  logoType: LogoType
) {
  const { fallback, getExpectedLogoUrl } = useLogos(brcyCode, locale);
  const [isFallback, setIsFallback] = useState(false);
  const fallbackLogo = fallback[logoType];
  const src = isFallback ? fallbackLogo.path : getExpectedLogoUrl(logoType);
  const altText = useAltTextForLogoType(logoType);
  useEffect(() => {
    let isCancelled = false;
    const img = new Image();
    img.src = getExpectedLogoUrl(logoType);

    img.onerror = () => {
      if (!isCancelled) {
        setIsFallback(true);
      }
    };
    return () => {
      isCancelled = true;
    };
  }, [getExpectedLogoUrl, logoType]);
  return {
    src,
    altText,
  };
}

export function useCurrentContextLogoInfo(logoType: LogoType) {
  const brcyCode = useBrcyCode();
  const locale = useLocale();
  return useLogoImageInfo(brcyCode, locale, logoType);
}

export function useCurrentContextLogos() {
  const brcyCode = useBrcyCode();
  const locale = useLocale();
  return useLogos(brcyCode, locale);
}

type BrcyImgProps = DetailedHTMLProps<
  ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  type: LogoType;
  ref?: Ref<HTMLImageElement>;
};

export function BrcyImg({ type, ref, ...rest }: BrcyImgProps) {
  const { src, altText } = useCurrentContextLogoInfo(type);
  return (
    // biome-ignore lint/performance/noImgElement:required for email signature
    <img
      ref={ref}
      style={{ height: "auto" }}
      src={src}
      alt={altText}
      {...rest}
    />
  );
}

function useAltTextForLogoType(logoType: LogoType) {
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:useLogos" });
  const altTextMap: Record<LogoType, string> = {
    Desktop: t("desktopAltText", "Desktop logo"),
    Mobile: t("mobileAltText", "Mobile logo"),
    EmailSignature: t("emailSignatureAltText", "Email signature logo"),
    EmailSignatureEv: t("emailSignatureEvAltText", "Email signature EV logo"),
  };
  return altTextMap[logoType];
}

export function BrcyLogo({
  type,
  width,
  height,
  priority = true,
  brcyCode: overrideBrcyCode,
}: {
  type: LogoType;
  width: number;
  height: number;
  priority?: boolean;
  brcyCode?: BRCYCodes;
}) {
  const code = useBrcyCode();
  const brcyCode = overrideBrcyCode ?? code;
  const locale = useLocale();
  const { src, altText } = useLogoImageInfo(brcyCode, locale, type);
  return (
    <NextImage
      unoptimized
      priority={priority}
      src={src}
      width={width}
      height={height}
      alt={altText}
      style={{
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
        objectPosition: "center",
      }}
    />
  );
}
